import { createSelector } from "@reduxjs/toolkit";
import { RootState } from "../index";
import type { ShipBuilderState } from "../../types/ship";
import { getDriveTons } from "../../constants/drives";
import {
  CrewRole,
  CREW_SALARIES,
  CREW_REDUCTION_MULTIPLIERS,
  CREW_REDUCTION_THRESHOLDS,
} from "../../types/crew";
import { selectAllOptionalSystems } from "./optionalSystemsSelectors";

// Base selectors
const selectShipBuilderState = (state: RootState): ShipBuilderState =>
  state.shipBuilder;

// Software-specific base selectors
const selectInstalledSoftware = createSelector(
  selectShipBuilderState,
  (state) => state.software?.installed || [],
);

const selectRunningSoftware = createSelector(
  selectShipBuilderState,
  (state) => state.software?.running || [],
);

// Software-specific derived selectors
const selectInstalledSoftwareById = createSelector(
  [selectInstalledSoftware, (state: RootState, packageId: string) => packageId],
  (installed, packageId) => installed.find((s) => s.packageId === packageId),
);

const selectRunningSoftwareById = createSelector(
  [selectRunningSoftware, (state: RootState, packageId: string) => packageId],
  (running, packageId) => running.find((s) => s.packageId === packageId),
);

// Export individual selectors
export const selectHull = createSelector(
  selectShipBuilderState,
  (state) => state.hull,
);

export const selectHullAndArmorStats = createSelector(
  selectShipBuilderState,
  (state) => state.hullAndArmorStats,
);

export const selectCrewMode = createSelector(
  selectShipBuilderState,
  (state) => state.crewMode,
);

export const selectCrewOverrides = createSelector(
  selectShipBuilderState,
  (state) => state.crewOverrides,
);

/**
 * Get the crew reduction multiplier based on ship size
 */
const getCrewReductionMultiplier = (shipSize: number): number => {
  if (shipSize < CREW_REDUCTION_THRESHOLDS.MEDIUM) {
    return CREW_REDUCTION_MULTIPLIERS.SMALL;
  } else if (shipSize < CREW_REDUCTION_THRESHOLDS.LARGE) {
    return CREW_REDUCTION_MULTIPLIERS.MEDIUM;
  } else if (shipSize < CREW_REDUCTION_THRESHOLDS.HUGE) {
    return CREW_REDUCTION_MULTIPLIERS.LARGE;
  } else if (shipSize < CREW_REDUCTION_THRESHOLDS.MASSIVE) {
    return CREW_REDUCTION_MULTIPLIERS.HUGE;
  } else {
    return CREW_REDUCTION_MULTIPLIERS.MASSIVE;
  }
};

export const selectMinimumCrew = createSelector(
  [
    selectShipBuilderState,
    selectCrewMode,
    (state: RootState) => selectAllOptionalSystems(state),
  ],
  (state, mode, allOptionals) => {
    // Extract ship data
    const shipSize = state.shipSize;
    const hasJumpDrive =
      state.drives?.some((d) => d.type === "jump" && d.rating > 0) || false;
    const smallCraftCount = state.smallCraftCount || 0;
    const weaponMounts = state.weaponMounts || [];
    // Use passengerConfig as the single source of truth for passenger counts
    const passengerCounts = {
      high: state.passengerConfig?.high || 0,
      middle: state.passengerConfig?.middle || 0,
      basic: state.passengerConfig?.basic || 0,
      low: state.passengerConfig?.low || 0,
    } as Record<string, number>;

    // Get crew reduction multiplier for large ships
    const crewReductionMultiplier = getCrewReductionMultiplier(shipSize);

    // Calculate weapon mount counts by type
    const getWeaponMountCounts = () => {
      const counts: Record<string, number> = {
        turret: 0,
        barbette: 0,
        smallBay: 0,
        mediumBay: 0,
        largeBay: 0,
        spinalMount: 0,
        pointDefense: 0,
        fixed: 0,
        screen: 0,
      };

      weaponMounts.forEach((mount) => {
        if (counts[mount.type] !== undefined) {
          counts[mount.type]++;
        }
      });

      return counts;
    };

    const weaponMountCounts = getWeaponMountCounts();

    // Table logic for Commercial/Military
    const getMinimum = (role: CrewRole): number => {
      if (mode === "Commercial") {
        switch (role) {
          case CrewRole.CAPTAIN:
            return 1;
          case CrewRole.PILOT:
            return 1 + smallCraftCount;
          case CrewRole.ASTROGATOR:
            return hasJumpDrive ? 1 : 0;
          case CrewRole.ENGINEER: {
            // Correct Traveller logic: sum drive tonnage, add power plant weight, divide by 35, add small craft
            let totalDriveTonnage = 0;
            if (state.drives && state.drives.length > 0) {
              for (const drive of state.drives) {
                totalDriveTonnage +=
                  require("../../constants/drives").getDriveTons(
                    drive.type,
                    drive.rating,
                    state.shipSize,
                  );
              }
            }
            if (
              state.powerPlant &&
              typeof state.powerPlant.weight === "number"
            ) {
              totalDriveTonnage += state.powerPlant.weight;
            }
            // Use the smallCraftCount from the new user-defined small craft system
            const baseEngineers = Math.max(
              Math.ceil(totalDriveTonnage / 35) + smallCraftCount,
              1,
            );
            // Apply large ships reduction
            return Math.ceil(baseEngineers * crewReductionMultiplier);
          }
          case CrewRole.MAINTENANCE: {
            const baseMaintenance = Math.ceil(shipSize / 1000);
            // Apply large ships reduction
            return Math.ceil(baseMaintenance * crewReductionMultiplier);
          }
          case CrewRole.GUNNER: {
            // Commercial: 1 per turret, barbette and screen (bay and spinal weapons require military crewing)
            const baseGunners =
              weaponMountCounts["turret"] +
              weaponMountCounts["barbette"] +
              weaponMountCounts["screen"];
            // Apply large ships reduction
            return Math.ceil(baseGunners * crewReductionMultiplier);
          }
          case CrewRole.STEWARD:
            return (
              Math.ceil((passengerCounts["high"] || 0) / 10) + // Round up per request
              Math.ceil((passengerCounts["middle"] || 0) / 100) // Round up per request
            );
          case CrewRole.ADMINISTRATOR: {
            const baseAdministrators = Math.ceil(shipSize / 2000);
            // Apply large ships reduction
            return Math.ceil(baseAdministrators * crewReductionMultiplier);
          }
          case CrewRole.SENSOR_OPERATOR: {
            const baseSensorOperators = Math.ceil(shipSize / 7500);
            // Apply large ships reduction
            return Math.ceil(baseSensorOperators * crewReductionMultiplier);
          }
          case CrewRole.MEDIC: {
            // 1 per 120 total (crew + passengers) + 1 per Medical Bay
            let totalCrew = 0;
            (Object.values(CrewRole) as CrewRole[]).forEach((role) => {
              if (role !== CrewRole.MEDIC && role !== CrewRole.OFFICER) {
                totalCrew += getMinimum(role);
              }
            });
            const totalPassengers = Object.values(passengerCounts || {}).reduce(
              (a, b) => a + b,
              0,
            );
            // Count Medical Bays using allOptionals
            let medicalBayCount = 0;
            if (Array.isArray(allOptionals)) {
              for (const system of allOptionals) {
                if (
                  system.type === "internal" &&
                  system.subtype === "internalMedicalBay"
                ) {
                  medicalBayCount += 1;
                }
              }
            }
            return (
              Math.ceil((totalCrew + totalPassengers) / 120) // Round up for safety - 1 medic for 0-120, 2 for 121-240, etc. + medicalBayCount
            );
          }
          case CrewRole.OFFICER: {
            // 1 per 10 total crew (Military)
            let totalCrew = 0;
            (Object.values(CrewRole) as CrewRole[]).forEach((role) => {
              if (role !== CrewRole.OFFICER) {
                totalCrew += getMinimum(role);
              }
            });
            return Math.floor(totalCrew / 10);
          }
          default:
            return 0;
        }
      } else {
        // Military mode
        switch (role) {
          case CrewRole.CAPTAIN:
            return 1;
          case CrewRole.PILOT:
            return 3 + smallCraftCount;
          case CrewRole.ASTROGATOR:
            return hasJumpDrive ? 1 : 0;
          case CrewRole.ENGINEER: {
            // Correct Traveller logic: sum drive tonnage, add power plant weight, divide by 35, add small craft
            let totalDriveTonnage = 0;
            if (state.drives && state.drives.length > 0) {
              for (const drive of state.drives) {
                totalDriveTonnage +=
                  require("../../constants/drives").getDriveTons(
                    drive.type,
                    drive.rating,
                    state.shipSize,
                  );
              }
            }
            if (
              state.powerPlant &&
              typeof state.powerPlant.weight === "number"
            ) {
              totalDriveTonnage += state.powerPlant.weight;
            }
            // Use the smallCraftCount from the new user-defined small craft system
            const baseEngineers = Math.max(
              Math.ceil(totalDriveTonnage / 35) + smallCraftCount,
              1,
            );
            // Apply large ships reduction
            return Math.ceil(baseEngineers * crewReductionMultiplier);
          }
          case CrewRole.MAINTENANCE: {
            const baseMaintenance = Math.ceil(shipSize / 500);
            // Apply large ships reduction
            return Math.ceil(baseMaintenance * crewReductionMultiplier);
          }
          case CrewRole.GUNNER: {
            // Military: 1 per small bay; 2 per turret, barbette, medium bay and screen; 4 per large bay; 1 per 100 tons of spinal mount weaponry
            const baseGunners =
              weaponMountCounts["smallBay"] +
              2 *
                (weaponMountCounts["turret"] +
                  weaponMountCounts["barbette"] +
                  weaponMountCounts["mediumBay"] +
                  weaponMountCounts["screen"]) +
              4 * weaponMountCounts["largeBay"] +
              Math.ceil(
                (weaponMounts || [])
                  .filter((mount) => mount.type === "spinalMount")
                  .reduce((total, mount) => total + mount.tons, 0) / 100,
              );
            // Apply large ships reduction
            return Math.ceil(baseGunners * crewReductionMultiplier);
          }
          case CrewRole.STEWARD:
            return (
              Math.floor((passengerCounts["high"] || 0) / 10) + // Round down per Traveller rules
              Math.floor((passengerCounts["middle"] || 0) / 100) // Round down per Traveller rules
            );
          case CrewRole.ADMINISTRATOR: {
            const baseAdministrators = Math.ceil(shipSize / 1000);
            // Apply large ships reduction
            return Math.ceil(baseAdministrators * crewReductionMultiplier);
          }
          case CrewRole.SENSOR_OPERATOR: {
            const baseSensorOperators = 3 * Math.ceil(shipSize / 7500);
            // Apply large ships reduction
            return Math.ceil(baseSensorOperators * crewReductionMultiplier);
          }
          case CrewRole.MEDIC: {
            // 1 per 120 total (crew + passengers) + 1 per Medical Bay
            let totalCrew = 0;
            (Object.values(CrewRole) as CrewRole[]).forEach((role) => {
              if (role !== CrewRole.MEDIC && role !== CrewRole.OFFICER) {
                totalCrew += getMinimum(role);
              }
            });
            const totalPassengers = Object.values(passengerCounts || {}).reduce(
              (a, b) => a + b,
              0,
            );
            // Count Medical Bays using allOptionals
            let medicalBayCount = 0;
            if (Array.isArray(allOptionals)) {
              for (const system of allOptionals) {
                if (
                  system.type === "internal" &&
                  system.subtype === "internalMedicalBay"
                ) {
                  medicalBayCount += 1;
                }
              }
            }
            return (
              Math.ceil((totalCrew + totalPassengers) / 120) // Round up for safety - 1 medic for 0-120, 2 for 121-240, etc. + medicalBayCount
            );
          }
          case CrewRole.OFFICER:
            return Math.floor(
              Object.values(weaponMountCounts || {}).reduce((a, b) => a + b, 0) / 10,
            );
          default:
            return 0;
        }
      }
    };

    // Build minimums from ship data only
    const minimums: Record<CrewRole, number> = {} as Record<CrewRole, number>;
    (Object.values(CrewRole) as CrewRole[]).forEach((role) => {
      minimums[role] = getMinimum(role);
    });
    return minimums;
  },
);

export const selectCrewSalaryTotal = createSelector(
  selectMinimumCrew,
  selectCrewOverrides,
  () => CREW_SALARIES,
  (minimums, overrides, salaries) => {
    let total = 0;
    (Object.values(CrewRole) as CrewRole[]).forEach((role) => {
      // Use assigned (override) if present, else minimum
      let assigned =
        overrides && overrides[role] !== undefined
          ? overrides[role]
          : minimums[role] || 0;
      assigned =
        typeof assigned === "number" && !isNaN(assigned) ? assigned : 0;
      total += assigned * (salaries[role]?.baseSalary || 0);
    });

    // Add Ship's Troops/Marines salary (Cr1,000 per marine)
    const marines =
      overrides && overrides["Ship's Troops/Marines"] !== undefined
        ? overrides["Ship's Troops/Marines"]
        : 0;
    const marinesSalary =
      typeof marines === "number" && !isNaN(marines) ? marines * 1000 : 0;
    total += marinesSalary;

    return total;
  },
);

export const selectStateroomConfig = createSelector(
  selectShipBuilderState,
  (state) => state.stateroomConfig,
);

export const selectPassengerConfig = (state: RootState) =>
  state.shipBuilder.passengerConfig;
export const selectPassengerCounts = (state: RootState) => {
  const config = state.shipBuilder.passengerConfig || {
    high: 0,
    middle: 0,
    basic: 0,
    low: 0,
  };
  return {
    high: config.high,
    middle: config.middle,
    basic: config.basic,
    low: config.low,
  };
};
export const selectPassengerCargoOverrides = (state: RootState) =>
  (state.shipBuilder.passengerConfig &&
    state.shipBuilder.passengerConfig.cargoOverrides) || {
    high: 1,
    middle: 100,
    basic: 10,
    low: 10,
  };

// Selector for stewards required (single source of truth)
export const selectStewardsRequired = createSelector(
  [selectShipBuilderState],
  (state) => {
    // Use passengerConfig instead of passengerCounts
    const high = state.passengerConfig?.high || 0;
    const middle = state.passengerConfig?.middle || 0;
    const stewardsForHigh = Math.ceil(high / 10); // Round up
    const stewardsForMiddle = Math.ceil(middle / 100); // Round up
    return {
      total: stewardsForHigh + stewardsForMiddle,
      high: stewardsForHigh,
      middle: stewardsForMiddle,
    };
  },
);

export const selectCrewComposition = createSelector(
  selectShipBuilderState,
  (state) => {
    const crew = state.crewComposition || {};
    return {
      captain: crew.captain ?? 0,
      pilot: crew.pilot ?? 0,
      astrogator: crew.astrogator ?? 0,
      engineer: crew.engineer ?? 0,
      maintenance: crew.maintenance ?? 0,
      gunner: crew.gunner ?? 0,
      steward: crew.steward ?? 0,
      administrator: crew.administrator ?? 0,
      sensorOperator: crew.sensorOperator ?? 0,
      medic: crew.medic ?? 0,
      officer: crew.officer ?? 0,
      marines: crew.marines ?? 0,
    };
  },
);

export const selectSupplyUnits = createSelector(
  selectShipBuilderState,
  (state) => state.supplyUnits
);

export const selectSupplyTons = createSelector(
  selectShipBuilderState,
  (state) => state.supplyTons
);

export const selectOverrideTechLevel = createSelector(
  selectShipBuilderState,
  (state) => state.settings.overrideTechLevel
);
export const selectOverrideWeight = createSelector(
  selectShipBuilderState,
  (state) => state.settings.overrideWeight
);
export const selectOverrideSize = createSelector(
  selectShipBuilderState,
  (state) => state.settings.overrideSize
);
export const selectOverridePower = createSelector(
  selectShipBuilderState,
  (state) => state.settings.overridePower
);
export const selectOverrideAllRestrictions = createSelector(
  selectShipBuilderState,
  (state) => state.settings.overrideAllRestrictions
);

export const selectTLOverrideActive = createSelector(
  selectShipBuilderState,
  (state) => !!(state.settings?.overrideTechLevel || state.settings?.overrideAllRestrictions)
);

export const selectSizeOverrideActive = createSelector(
  selectShipBuilderState,
  (state) => !!(state.settings?.overrideSize || state.settings?.overrideAllRestrictions)
);

export const selectPowerOverrideActive = createSelector(
  selectShipBuilderState,
  (state) => !!(state.settings?.overridePower || state.settings?.overrideAllRestrictions)
);

export const selectWeightOverrideActive = createSelector(
  selectShipBuilderState,
  (state) => !!(state.settings?.overrideWeight || state.settings?.overrideAllRestrictions)
);

export const selectCrewAssignments = createSelector(
  selectShipBuilderState,
  (state) => {
    // This is a placeholder implementation
    // In a real implementation, this would return the crew assignments from the state
    return {} as Record<string, Record<number, number>>;
  }
);

export const selectors = {
  // Basic selectors
  selectShipBuilder: selectShipBuilderState,

  selectCurrentStep: createSelector(
    selectShipBuilderState,
    (state) => state.currentStep,
  ),

  selectShipSize: createSelector(
    selectShipBuilderState,
    (state) => state.shipSize,
  ),

  selectTechLevel: createSelector(
    selectShipBuilderState,
    (state) => state.techLevel,
  ),

  // Ship component selectors
  selectHull,

  selectHullConfiguration: createSelector(
    selectShipBuilderState,
    (state) => state.hull?.configuration,
  ),

  selectArmor: createSelector(selectShipBuilderState, (state) => state.armor),

  selectDrives: createSelector(selectShipBuilderState, (state) => state.drives),

  selectPowerPlant: createSelector(
    selectShipBuilderState,
    (state) => state.powerPlant,
  ),

  selectFuel: createSelector(
    selectShipBuilderState,
    (state) => ({ weight: state.fuelTanks || 0 }),
  ),

  selectBridge: createSelector(selectShipBuilderState, (state) => state.bridge),

  selectSensors: createSelector(
    selectShipBuilderState,
    (state) => state.sensors,
  ),

  selectWeapons: createSelector(
    selectShipBuilderState,
    (state) => state.weapons,
  ),

  selectOptionalSystems: createSelector(
    selectShipBuilderState,
    (state) => state.optionalSystems,
  ),

  selectCrew: createSelector(selectShipBuilderState, (state) => state.crew),

  selectStaterooms: createSelector(
    selectShipBuilderState,
    (state) => state.staterooms,
  ),

  selectCargo: createSelector(selectShipBuilderState, (state) => state.cargo),

  selectFinalDetails: createSelector(
    selectShipBuilderState,
    (state) => state.finalDetails,
  ),

  // Stats selectors
  selectShipStats: createSelector(
    selectShipBuilderState,
    (state) => state.shipStats,
  ),

  selectTotalWeight: createSelector(
    selectShipBuilderState,
    (state) => state.shipStats?.totalWeight || 0,
  ),

  selectPowerRequirements: createSelector(
    selectShipBuilderState,
    (state) => state.shipStats?.powerRequirements || { normalOperations: 0, jumpOperations: 0, maximumLoad: 0 },
  ),

  selectCargoSpace: createSelector(
    selectShipBuilderState,
    (state) => state.shipStats?.cargoSpace || 0,
  ),

  selectCrewRequirements: createSelector(
    selectShipBuilderState,
    (state) => state.shipStats?.crewRequirements || { minimum: 0, recommended: 0 },
  ),

  selectTotalCost: createSelector(
    selectShipBuilderState,
    (state) => state.shipStats?.totalCost || 0,
  ),

  // Validation selectors
  selectValidation: createSelector(
    selectShipBuilderState,
    (state) => state.validation,
  ),

  selectSmallCraftCount: createSelector(
    selectShipBuilderState,
    (state) => state.smallCraftCount || 0,
  ),

  selectWeaponCounts: createSelector(
    selectShipBuilderState,
    (state) => state.weaponCounts || {},
  ),

  selectPassengerCounts: createSelector(
    selectShipBuilderState,
    (state) => state.passengerCounts || {},
  ),

  selectCrewComposition: createSelector(selectShipBuilderState, (state) => {
    const crew = state.crewComposition || {};
    return {
      captain: crew.captain ?? 0,
      pilot: crew.pilot ?? 0,
      astrogator: crew.astrogator ?? 0,
      engineer: crew.engineer ?? 0,
      maintenance: crew.maintenance ?? 0,
      gunner: crew.gunner ?? 0,
      steward: crew.steward ?? 0,
      administrator: crew.administrator ?? 0,
      sensorOperator: crew.sensorOperator ?? 0,
      medic: crew.medic ?? 0,
      officer: crew.officer ?? 0,
      marines: crew.marines ?? 0,
    };
  }),

  // Ship selectors (nested object for compatibility)
  shipSelectors: {
    selectShipSize: createSelector(
      selectShipBuilderState,
      (state) => state.shipSize,
    ),
    selectDrives: createSelector(
      selectShipBuilderState,
      (state) => state.drives,
    ),
  },

  // Software-specific selectors
  selectInstalledSoftware,
  selectRunningSoftware,
  selectInstalledSoftwareById,
  selectRunningSoftwareById,

  selectIsSoftwareInstalled: createSelector(
    [selectInstalledSoftwareById],
    (installed) => installed?.installed ?? false,
  ),

  selectIsSoftwareRunning: createSelector(
    [selectRunningSoftwareById],
    (running) => running?.running ?? false,
  ),

  selectCrewMode,
  selectCrewOverrides,
  selectMinimumCrew,
  selectCrewSalaryTotal,
  selectStateroomConfig,
  selectStewardsRequired,
  selectComputer: createSelector(
    selectShipBuilderState,
    (state) => state.computer,
  ),
  selectSoftware: createSelector(
    selectShipBuilderState,
    (state) => state.software,
  ),

  selectDefensiveSystems: createSelector(
    selectShipBuilderState,
    (state) => state.defensiveSystems || [],
  ),
  selectSupplyUnits,
  selectSupplyTons,
  selectOverrideTechLevel,
  selectOverrideWeight,
  selectOverrideSize,
  selectOverridePower,
  selectOverrideAllRestrictions,
  selectTLOverrideActive,
  selectWeightOverrideActive,
  selectSizeOverrideActive,
  selectPowerOverrideActive,
  selectCrewAssignments,
};

// Utility to get tonnage for a protectable system ID
export function getSystemTonnageFromState(
  state: ShipBuilderState,
  id: string,
): number {
  // Ensure state and optionalSystems are defined
  if (!state || !state.optionalSystems) {
    return 0;
  }
  switch (id) {
    case "bridge":
      return state.bridge?.weight || 0;
    case "computer":
      return 0;
    case "sensors":
      return state.sensors?.tons || 0;
    case "powerPlant":
      return state.powerPlant?.weight || 0;
    case "maneuverDrive": {
      const drive = state.drives.find((d) => d.type === "maneuver");
      return drive ? getDriveTons(drive.type, drive.rating, state.shipSize) : 0;
    }
    case "jumpDrive": {
      const drive = state.drives.find((d) => d.type === "jump");
      return drive ? getDriveTons(drive.type, drive.rating, state.shipSize) : 0;
    }
    case "reactionDrive": {
      const drive = state.drives.find((d) => d.type === "reaction");
      return drive ? getDriveTons(drive.type, drive.rating, state.shipSize) : 0;
    }
    case "fuelTanks":
      return state.fuel?.weight || 0;
    case "staterooms":
      return state.staterooms?.reduce((sum, s) => sum + (s.tons || 0), 0) || 0;
    case "cargo":
      return state.cargo?.weight || 0;
    case "weapons":
      return state.weapons?.reduce((sum, w) => sum + (w.tons || 0), 0) || 0;
    case "hangar":
      return state.optionalSystems
        .filter((s) => s.type === "hangar")
        .reduce((sum, s) => sum + (s.tonnage || 0), 0);
    case "medicalBay":
      return state.optionalSystems
        .filter(
          (s) => s.type === "internal" && s.subtype === "internalMedicalBay",
        )
        .reduce((sum, s) => sum + (s.tonnage || 0), 0);
    case "droneBay":
      return state.optionalSystems
        .filter((s) => s.type === "drone")
        .reduce((sum, s) => sum + (s.tonnage || 0), 0);
    case "externalSystems":
      return state.optionalSystems
        .filter((s) => s.type === "external")
        .reduce((sum, s) => sum + (s.tonnage || 0), 0);
    case "internalSystems":
      return state.optionalSystems
        .filter((s) => s.type === "internal")
        .reduce((sum, s) => sum + (s.tonnage || 0), 0);
    case "modularHull":
      return state.optionalSystems
        .filter((s) => s.type === "modularHull")
        .reduce((sum, s) => sum + (s.tonnage || 0), 0);
    default:
      return 0;
  }
}
